export enum USER_ROLE_ENUM {
  ADMIN = 'admin',
  PATIENT = 'patient',
  CLINICIAN = 'clinician',
  NURSINGSTAFF = 'nursing staff',
}

// Role slugs as GET /v1/access and GET /v1/roles return them — verbatim
// role.type values from the new BE (rpms pkg/constant/const.proto RL_* `(slug)`
// options; see data/v1/role.go "slug (r.Type, e.g. \"#Role.Nurse\")").
//
// The legacy numeric ids (ADMINVALUE=1 / CLINICIANVALUE=2 / NURSINGSTAFFVALUE=4)
// that used to live here are GONE as of batch 006, and no compatibility mapping
// may be reintroduced (ADR-7): the new enum numbers RL_NURSE=3 / RL_PATIENT=4
// overlap the old ones, so any old-id → new-id translation turns a nurse into a
// patient. Roles cross the wire as slugs, everywhere, in both directions.
export const ROLE_SLUG_ADMIN = '#Role.Admin';
export const ROLE_SLUG_CLINICIAN = '#Role.Clinician';
export const ROLE_SLUG_NURSE = '#Role.Nurse';

// The three assignable employee roles, in the order the admin surface shows
// them. GET /v1/roles serves the same set (it is the projection of the server's
// own write-validation whitelist), so this array is the display order and the
// drift gate — not a second source of truth.
export const EMPLOYEE_ROLE_SLUGS = [
  ROLE_SLUG_ADMIN,
  ROLE_SLUG_CLINICIAN,
  ROLE_SLUG_NURSE,
] as const;

// Slug → i18n key. The server deliberately serves slugs WITHOUT display names
// (ADR-7): Danish labels already exist in this repo's compile-time bundle
// (i18n/da.json "administrator"/"clinician"/"nursing staff"), and serving names
// from the BE would regress da users to English.
//
// A slug must NEVER be handed to lt() directly: i18n.ts sets no `keySeparator`,
// so i18next's default '.' applies and a slug like "#Role.Nurse" is parsed as a
// nested path, misses, and renders the raw key string to the user.
export const ROLE_LABEL_KEY: Record<string, string> = {
  [ROLE_SLUG_ADMIN]: 'administrator',
  [ROLE_SLUG_CLINICIAN]: 'clinician',
  [ROLE_SLUG_NURSE]: 'nursing staff',
};

// SSO staff hold no PASSWORD credential, so `username` — which IS that
// credential's identifier — is an empty string for them on the wire. The admin
// surface shows the e-mail's local part in its place.
//
// DISPLAY ONLY. It is never stored and never submitted: the backend keeps the
// column NULL on purpose (sorting by username pins these rows last in both
// directions, and inventing a value would put a non-existent credential's
// identifier in a field that is defined as exactly that). Sorting therefore
// still follows the server's NULL-last order, NOT this derived string — do not
// "unify" the two.
export const derivedUsername = (email: string) => email.split('@')[0] ?? '';

export const TOKEN_INFO = {
  ACCESS_TOKEN: 'ACCESS_TOKEN',
  REFRESH_TOKEN: 'REFRESH_TOKEN',
  TOKEN_VERIFIER: 'tokenverifier',
};

export enum PasswordFactorStatus {
  default,
  pass,
  error,
}

export type PasswordFactor = {
  labelKey: string;
  rule: RegExp;
  status: PasswordFactorStatus;
};
